import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import usePageMeta from "@/hooks/usePageMeta";
import { movies } from "@/mocks/movies";

export default function Detail() {
  const { id } = useParams();
  const movie = movies.find((m) => `${m.id}` === id);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2200);
    return () => clearTimeout(timer);
  }, [copied]);

  usePageMeta({
    title: movie ? `${movie.title} - 555电影` : "电影信息不存在 - 555电影",
    description: movie
      ? `${movie.title}（${movie.year}，${movie.region}）：${movie.genres.join("、")}类型影片，评分 ${movie.rating}，导演 ${movie.director}。${movie.synopsis.slice(0, 60)}...`
      : "该电影信息不存在或已下架，请返回电影分类或首页浏览其他影片。",
    keywords: movie
      ? `${movie.title},${movie.genres.join(",")},${movie.region}电影,555电影`
      : "电影详情,555电影",
  });

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
    } catch {
      setCopied(true);
    }
  };

  if (!movie) {
    return (
      <div className="mx-auto flex w-full max-w-screen-xl flex-col items-center px-4 py-24 text-center md:px-6">
        <span className="mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-emotion-sad-line text-3xl"></i>
        </span>
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          电影信息不存在或已下架
        </h1>
        <p className="mt-3 max-w-md text-sm text-foreground-600">
          你访问的影片可能已被移除，或链接有误。可以返回分类页继续浏览其他精彩影片。
        </p>
        <div className="mt-7 flex flex-wrap justify-center gap-3">
          <Link
            to="/category"
            className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            返回电影分类
          </Link>
          <Link
            to="/"
            className="whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-100"
          >
            回到首页
          </Link>
        </div>
      </div>
    );
  }

  const related = movies
    .filter((m) => m.id !== movie.id && m.genres.some((g) => movie.genres.includes(g)))
    .sort((a, b) => b.rating - a.rating)
    .slice(0, 4);

  const infoRows = [
    { label: "导演", value: movie.director },
    { label: "主演", value: movie.cast.join(" / ") },
    { label: "类型", value: movie.genres.join(" / ") },
    { label: "上映时间", value: `${movie.year} 年` },
    { label: "时长", value: `${movie.duration} 分钟` },
    { label: "地区", value: movie.region },
    { label: "原名", value: movie.originalTitle },
  ];

  return (
    <div className="w-full animate-fadeIn">
      {/* Banner */}
      <div className="relative w-full overflow-hidden bg-primary-950">
        <img
          src={movie.cover}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover object-top opacity-30 blur-sm"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-primary-950/70 via-primary-950/80 to-primary-950"></div>

        <div className="relative z-10 mx-auto w-full max-w-screen-xl px-4 py-8 md:px-6 md:py-12">
          <div className="mb-5 text-background-300">
            <Breadcrumb
              items={[
                { label: "首页", to: "/" },
                { label: "电影分类", to: "/category" },
                { label: movie.title },
              ]}
            />
          </div>

          <div className="flex flex-col gap-6 md:flex-row md:gap-8">
            <div className="w-40 shrink-0 self-center sm:w-48 md:self-start lg:w-56">
              <div className="aspect-[2/3] w-full overflow-hidden rounded-lg border border-background-50/20 bg-background-200">
                <img
                  src={movie.cover}
                  alt={`${movie.title} 电影海报`}
                  title={`${movie.title} - 555电影`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-3">
                <span className="rounded-full bg-secondary-500 px-3 py-1 text-xs font-semibold text-background-50">
                  编辑推荐
                </span>
                <span className="flex items-center gap-1 rounded-full bg-accent-500 px-3 py-1 text-xs font-bold text-foreground-950">
                  <span className="flex h-3.5 w-3.5 items-center justify-center">
                    <i className="ri-star-fill text-xs"></i>
                  </span>
                  {movie.rating.toFixed(1)}
                </span>
              </div>

              <h1 className="mt-3 font-heading text-3xl font-bold text-background-50 md:text-4xl">
                {movie.title}
              </h1>
              <p className="mt-1 text-sm text-background-400">{movie.originalTitle}</p>

              <div className="mt-4 flex flex-wrap gap-2">
                {movie.genres.map((g) => (
                  <Link
                    key={g}
                    to={`/category?genre=${encodeURIComponent(g)}`}
                    className="rounded-full bg-primary-800 px-3 py-1 text-xs text-background-100 transition-colors hover:bg-primary-700"
                  >
                    {g}
                  </Link>
                ))}
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={handleShare}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/30 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10"
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-share-line text-base"></i>
                  </span>
                  复制链接分享
                </button>
                <Link
                  to="/category"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-400"
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-apps-2-line text-base"></i>
                  </span>
                  更多同类影片
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="mx-auto w-full max-w-screen-xl px-4 py-10 md:px-6 md:py-12">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <section className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="font-heading text-xl font-bold text-foreground-950">剧情简介</h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
                {movie.synopsis}
              </p>
            </section>

            <section className="mt-6">
              <div className="mb-5 flex items-center gap-2">
                <span className="h-5 w-1 rounded-full bg-accent-500"></span>
                <h2 className="font-heading text-xl font-bold text-foreground-950">相关推荐</h2>
              </div>
              {related.length === 0 ? (
                <p className="rounded-lg border border-dashed border-background-300 bg-background-50 py-10 text-center text-sm text-foreground-500">
                  暂无相关推荐
                </p>
              ) : (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
                  {related.map((m) => (
                    <MovieCard key={m.id} movie={m} />
                  ))}
                </div>
              )}
            </section>
          </div>

          <aside className="lg:col-span-1">
            <section className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="font-heading text-lg font-bold text-foreground-950">影片信息</h2>
              <dl className="mt-4 flex flex-col gap-3">
                {infoRows.map((row) => (
                  <div key={row.label} className="flex gap-3 border-b border-background-100 pb-3 last:border-b-0 last:pb-0">
                    <dt className="w-20 shrink-0 text-sm text-foreground-500">{row.label}</dt>
                    <dd className="min-w-0 flex-1 text-sm font-medium text-foreground-900">
                      {row.value}
                    </dd>
                  </div>
                ))}
              </dl>
            </section>

            <section className="mt-6 rounded-lg bg-primary-950 p-5 md:p-6">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold text-background-50">
                <span className="flex h-5 w-5 items-center justify-center text-accent-500">
                  <i className="ri-price-tag-3-line text-base"></i>
                </span>
                分类标签
              </h2>
              <div className="mt-4 flex flex-wrap gap-2">
                {movie.genres.map((g) => (
                  <Link
                    key={g}
                    to={`/category?genre=${encodeURIComponent(g)}`}
                    className="rounded-full bg-primary-800 px-3 py-1 text-xs text-background-100 transition-colors hover:bg-accent-500 hover:text-primary-950"
                  >
                    {g}
                  </Link>
                ))}
                <Link
                  to={`/category?region=${encodeURIComponent(movie.region)}`}
                  className="rounded-full bg-primary-800 px-3 py-1 text-xs text-background-100 transition-colors hover:bg-accent-500 hover:text-primary-950"
                >
                  {movie.region}
                </Link>
                <Link
                  to={`/category?year=${movie.year}`}
                  className="rounded-full bg-primary-800 px-3 py-1 text-xs text-background-100 transition-colors hover:bg-accent-500 hover:text-primary-950"
                >
                  {movie.year}
                </Link>
              </div>
            </section>
          </aside>
        </div>

        <div className="mt-8 text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-arrow-left-line text-sm"></i>
            </span>
            返回首页
          </Link>
        </div>
      </div>

      {copied && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 animate-fadeUp rounded-md bg-primary-950 px-5 py-3 text-sm font-medium text-background-50">
          <span className="mr-2 inline-flex items-center gap-1 text-accent-500">
            <i className="ri-checkbox-circle-fill"></i>
          </span>
          链接已复制，快去分享给朋友吧
        </div>
      )}
    </div>
  );
}